/// <amd-module name="Wit/WorkItemForm/History/Diffs/UpdateDiffs" />
import * as React from "react";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { usePageContext } from "VSS/Platform/Layout";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { FieldType } from "Wit/Common/Generated/Constants";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { IFieldLookup } from "Wit/WorkItemDataManager/Contracts";
import * as Resources from "Wit/WorkItemForm/Resources";
import { ILinkIdentifier, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { isSameLinkIdentifier } from "Wit/WorkItemLinksRegistration/LinkUtils";
import { resolveAllArtifacts } from "Wit/WorkItemLinksRegistration/resolveArtifacts";
import { PageSizes } from "Wit/WorkItemModel/OM";
import { isEmpty } from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { useFieldMetadata, useWorkItem } from "../../Common/Hooks";
import { IHistoryItem } from "../../Common/Services";
import { ChangeType } from "../ChangeType";
import { isAttachmentUpdate, isCalculatedField, isLinkUpdate } from "../HistoryUtils";
import { AttachmentDiff, IAttachmentUpdateProps } from "./AttachmentDiff";
import { CommentDiff, ICommentDiffProps } from "./CommentDiff";
import { FieldDiff, IFieldDiffProps } from "./FieldDiff";
import { HtmlFieldDiff, IHtmlFieldDiffProps } from "./HtmlFieldDiff";
import { ILinkUpdateProps, LinkDiff } from "./LinkDiff";
import { ITestStepDiffProps, TestStepDiff } from "./TestStepDiff";

export interface IUpdateDiffsProps {
    update: IHistoryItem;
}

export function UpdateDiffs({ update }: IUpdateDiffsProps) {
    const pageContext = usePageContext();
    const workItem = useWorkItem();
    const fieldLookup = useFieldMetadata();
    const historyLinksOptimizationsEnabled = isFeatureFlagEnabled(pageContext, FeatureFlags.HistoryLinksOptimizationsEnabled);

    const [data, setData] = React.useState<{ linkIdentifiers: ILinkIdentifier[] | undefined; resolveArtifacts: IResolvedLinkDisplayData[] | undefined }>({ linkIdentifiers: [], resolveArtifacts: [] });

    React.useEffect(() => {
        if (!historyLinksOptimizationsEnabled) {
            return;
        }

        pageContext
            .getService<IWorkItemLinksService>("IWorkItemLinksService")
            .getLinkIdentifiers(workItem, { includeDeleted: true })
            .then(linkIdentifiers => {
                if (!linkIdentifiers?.length || linkIdentifiers.length > PageSizes.QUERY) {
                    setData({ linkIdentifiers: linkIdentifiers, resolveArtifacts: [] });
                    return;
                }

                resolveAllArtifacts(linkIdentifiers, workItem.id, pageContext).then(resolveArtifacts => {
                    setData({ linkIdentifiers: linkIdentifiers, resolveArtifacts: resolveArtifacts });
                });
            });
    }, []);

    const { comments, fields, htmlFields, attachmentUpdates, linkUpdates, testStepsFields } = extractDiffLists(update, fieldLookup);

    return (
        <div className="update-diffs padding-16">
            {comments.length > 0 && (
                <div className="update-comments">
                    {comments.map(props => (
                        <CommentDiff {...props} />
                    ))}
                </div>
            )}

            {fields.length > 0 && (
                <div className="update-field-diffs">
                    {fields.map(props => (
                        <FieldDiff {...props} />
                    ))}
                </div>
            )}

            {htmlFields.length > 0 && (
                <div className="update-html-field-diffs">
                    {htmlFields.map(props => (
                        <HtmlFieldDiff {...props} />
                    ))}
                </div>
            )}

            {testStepsFields.length > 0 && (
                <div className="update-html-field-diffs">
                    {testStepsFields.map(props => (
                        <TestStepDiff {...props} />
                    ))}
                </div>
            )}

            {attachmentUpdates.length > 0 && (
                <div className="update-attachments">
                    <div className="update-diffs-section-header font-size-ml font-weight-semibold margin-bottom-8">{Resources.Attachments}</div>
                    {attachmentUpdates.map(props => (
                        <AttachmentDiff {...props} />
                    ))}
                </div>
            )}

            {!historyLinksOptimizationsEnabled && linkUpdates.length > 0 && (
                <div className="update-links">
                    <div className="update-diffs-section-header font-size-ml font-weight-semibold margin-bottom-8">{Resources.Links}</div>
                    {linkUpdates.map(props => {
                        return <LinkDiff historyLinksOptimizationsEnabled={historyLinksOptimizationsEnabled} {...props} />
                    })}
                </div>
            )}

            {historyLinksOptimizationsEnabled && data.linkIdentifiers && data.resolveArtifacts && linkUpdates.length > 0 && (
                <div className="update-links">
                    <div className="update-diffs-section-header font-size-ml font-weight-semibold margin-bottom-8">{Resources.Links}</div>
                    {linkUpdates.map(props => {
                        const matchingLinkIdentifier = data.linkIdentifiers!.find(l => {
                            return props.link.url === l.FilePath || props.link.url.endsWith(`/_apis/wit/workItems/${l.ID}`);
                        });

                        const resolvedArtifact = matchingLinkIdentifier && data.resolveArtifacts!.find(l => {
                            return isSameLinkIdentifier(l.linkIdentifier, matchingLinkIdentifier);
                        });

                        return <LinkDiff historyLinksOptimizationsEnabled={historyLinksOptimizationsEnabled} resolvedArtifact={resolvedArtifact} matchingLinkIdentifier={matchingLinkIdentifier} {...props} />
                    })}
                </div>
            )}
        </div>
    );
}

export interface IUpdateDiffLists {
    comments: ICommentDiffProps[];
    fields: IFieldDiffProps[];
    htmlFields: IHtmlFieldDiffProps[];
    attachmentUpdates: IAttachmentUpdateProps[];
    linkUpdates: ILinkUpdateProps[];
    testStepsFields: ITestStepDiffProps[];
}

const testStepsFieldName = "Microsoft.VSTS.TCM.Steps";

export function extractDiffLists(update: IHistoryItem, fieldLookup: IFieldLookup): IUpdateDiffLists {
    const fieldEntries = Object.entries(update.fields ?? {});

    const comments = update.commentEdit
        ? [
              {
                  key: `workItem-${update.workItemId}-update-${update.id}-comment-${update.commentEdit.commentId}-version-${update.commentEdit.commentVersion}`,
                  text: update.commentEdit.text,
                  previousText: update.commentEdit.previousText,
                  format: update.commentEdit.format
              }
          ]
        : [];

    const shouldShowInFieldDiffs = (refName: string) =>
        !isCalculatedField(refName, fieldLookup) && fieldLookup[refName]?.type !== FieldType.Html && fieldLookup[refName]?.type !== FieldType.History;

    const fields = fieldEntries
        .filter(([refName, fieldUpdate]) => (!isEmpty(fieldUpdate.oldValue) || !isEmpty(fieldUpdate.newValue)) && shouldShowInFieldDiffs(refName))
        .map(([refName, fieldUpdate]) => ({
            key: `workItem-${update.workItemId}-update-${update.id}-field-${refName}`,
            refName: refName,
            fieldUpdate: fieldUpdate
        }));

    const shouldShowInTestStepsFieldDiffs = (refName: string) => refName === testStepsFieldName;
    const shouldShowInHtmlFieldDiffs = (refName: string) => fieldLookup[refName]?.type === FieldType.Html;

    const htmlFields = fieldEntries
        .filter(
            ([refName, fieldUpdate]) =>
                (!isEmpty(fieldUpdate.oldValue) || !isEmpty(fieldUpdate.newValue)) &&
                shouldShowInHtmlFieldDiffs(refName) &&
                !shouldShowInTestStepsFieldDiffs(refName)
        )
        .map(([refName, fieldUpdate]) => ({
            key: `workItem-${update.workItemId}-update-${update.id}-field-${refName}`,
            refName: refName,
            fieldUpdate: fieldUpdate
        }));

    const attachmentUpdates = [
        ...(update.relations?.added ?? []).filter(isAttachmentUpdate).map(
            addedLink =>
                ({
                    key: `workItem-${update.workItemId}-update-${update.id}-added-attachment-${addedLink.url}`,
                    attachment: addedLink,
                    changeType: ChangeType.Added
                } as const)
        ),
        ...(update.relations?.updated ?? []).filter(isAttachmentUpdate).map(
            addedLink =>
                ({
                    key: `workItem-${update.workItemId}-update-${update.id}-updated-attachment-${addedLink.url}`,
                    attachment: addedLink,
                    changeType: ChangeType.Updated
                } as const)
        ),
        ...(update.relations?.removed ?? []).filter(isAttachmentUpdate).map(
            addedLink =>
                ({
                    key: `workItem-${update.workItemId}-update-${update.id}-removed-attachment-${addedLink.url}`,
                    attachment: addedLink,
                    changeType: ChangeType.Removed
                } as const)
        )
    ];

    const linkUpdates = [
        ...(update.relations?.added ?? []).filter(isLinkUpdate).map(
            addedLink =>
                ({
                    key: `workItem-${update.workItemId}-update-${update.id}-added-link-${addedLink.url}`,
                    link: addedLink,
                    changeType: ChangeType.Added
                } as const)
        ),
        ...(update.relations?.updated ?? []).filter(isLinkUpdate).map(
            updatedLink =>
                ({
                    key: `workItem-${update.workItemId}-update-${update.id}-updated-link-${updatedLink.url}`,
                    link: updatedLink,
                    changeType: ChangeType.Updated
                } as const)
        ),
        ...(update.relations?.removed ?? []).filter(isLinkUpdate).map(
            removedLink =>
                ({
                    key: `workItem-${update.workItemId}-update-${update.id}-removed-link-${removedLink.url}`,
                    link: removedLink,
                    changeType: ChangeType.Removed
                } as const)
        )
    ];

    const testStepsFields: ITestStepDiffProps[] = fieldEntries
        .filter(
            ([refName, fieldUpdate]) => (!isEmpty(fieldUpdate.oldValue) || !isEmpty(fieldUpdate.newValue)) && shouldShowInTestStepsFieldDiffs(refName)
        )
        .map(([refName, fieldUpdate]) => ({
            key: `workItem-${update.workItemId}-update-${update.id}-field-${refName}`,
            refName: refName,
            fieldUpdate: fieldUpdate,
            attachmentUpdates,
            linkUpdates
        }));

    return { comments, fields, htmlFields, attachmentUpdates, linkUpdates, testStepsFields };
}